import { useState, useEffect, useCallback, useRef } from 'react';
import { usePermissions } from '@/contexts/unified-permissions';
import { useNetworkStatus } from '@/contexts/offline/hooks/useOfflineState';
import type { UseUnreadChatCountReturn, ChatRealtimeSubscription } from '../types';
import { getUnreadMessageCount, subscribeToUnreadCount } from '../services/chatService';

/**
 * Hook for managing unread chat message count
 * Updates in realtime and used for FAB badge display
 */
export const useUnreadChatCount = (): UseUnreadChatCountReturn => {
  const { user } = usePermissions();
  const { isOnline } = useNetworkStatus();
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const subscriptionRef = useRef<ChatRealtimeSubscription | null>(null);

  // Load initial unread count
  const loadUnreadCount = useCallback(async () => {
    if (!user?.id) {
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    // Skip loading if offline
    if (!isOnline) {
      console.log('Offline: Skipping unread count load');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const count = await getUnreadMessageCount(user.id);
      setUnreadCount(count);
    } catch (err) {
      const errorMessage = err instanceof Error ? err : new Error('Failed to load unread count');
      console.error('Error loading unread count:', errorMessage);
      setError(errorMessage);
      // Don't reset count on error to avoid flickering
    } finally {
      setLoading(false);
    }
  }, [user?.id, isOnline]);

  // Handle unread count changes from realtime subscription
  const handleCountChange = useCallback((newCount: number) => {
    setUnreadCount(newCount);
    setError(null); // Clear any previous errors on successful update
  }, []);

  // Handle realtime subscription errors
  const handleRealtimeError = useCallback((err: Error) => {
    console.error('Unread count realtime error:', err);
    setError(err);
    // Don't show toast for realtime errors as they're often temporary
    // The count will be refreshed on next app focus or manual refresh
  }, []);

  // Setup realtime subscription for unread count updates
  useEffect(() => {
    if (!user?.id || !isOnline) {
      return;
    }

    // Clean up existing subscription
    if (subscriptionRef.current) {
      subscriptionRef.current.cleanup();
      subscriptionRef.current = null;
    }

    // Create new subscription only when online
    try {
      const subscription = subscribeToUnreadCount(
        user.id,
        handleCountChange,
        handleRealtimeError
      );
      subscriptionRef.current = subscription;
    } catch (err) {
      console.error('Error setting up unread count subscription:', err);
      setError(err instanceof Error ? err : new Error('Failed to setup realtime count updates'));
    }

    // Cleanup on unmount or dependency change
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.cleanup();
        subscriptionRef.current = null;
      }
    };
  }, [user?.id, isOnline, handleCountChange, handleRealtimeError]);

  // Load initial count when user changes
  useEffect(() => {
    loadUnreadCount();
  }, [loadUnreadCount]);

  // Refresh count when app regains focus (fallback for realtime issues)
  useEffect(() => {
    const handleFocus = () => {
      if (user?.id && isOnline) {
        loadUnreadCount();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loadUnreadCount, user?.id, isOnline]);

  // Cleanup subscription on unmount
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.cleanup();
      }
    };
  }, []);

  return {
    unreadCount,
    loading,
    error,
  };
};
